import { bodyFont, colours, fontFamilyImportUrl, headerFont } from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import "@affinity/Store/Reducers";
import { setPostStyleGuideTheme, setPreStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import Info from "@insite/mobius/Icons/Info";
import { css } from "styled-components";

import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";

// Load all custom widgets so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
const widgets = require.context("./Widgets", true, /\.tsx$/);
const onHotWidgetReplace = addWidgetsFromContext(widgets);
if (module.hot) {
    module.hot.accept(widgets.id, () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// Load all custom pages so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
const pages = require.context("./Pages", true, /\.tsx$/);
const onHotPageReplace = addPagesFromContext(pages);
if (module.hot) {
    module.hot.accept(pages.id, () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
}

// Load all handlers so they are included in the bundle.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

// Load all the extensions to the base widgets. This includes style extensions.
// const widgetExtensions = require.context('./WidgetExtensions', true);
// widgetExtensions.keys().forEach(key => widgetExtensions(key));
setPreStyleGuideTheme({
    globalStyle: {
        css: css`
            main:not(.a) {
                overflow: visible;
                padding-top: 0;
                padding: 0 ${styleHelpers.rem(20)} 0;
                @media (min-width: ${styleHelpers.breakpointWidth("md")}) {
                    padding: 0 ${styleHelpers.rem(40)} 0;
                }
                @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                    padding: 0 ${styleHelpers.rem(60)} 0;
                }
            }
            body {
                overflow-x: hidden;
            }
            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
                font-family: ${headerFont};
            }
            button:disabled {
                background: ${`${colours.darkBlue}33`}!important;
                color: ${colours.darkBlue}!important;
                border: 2px solid ${colours.darkBlue}!important;
            }
            html{
                scroll-behaviour: smooth;
                @media (prefers-reduced-motion: reduced){
                    scroll-behaviour: auto;
                }
            }
        `,
    },
    colors: {
        primary: {
            main: colours.lightGreen,
            contrast: colours.black,
        },
        secondary: {
            main: colours.darkBlue,
            contrast: colours.lightBlue,
        },
        success: {
            main: colours.midGreen,
        },
        danger: {
            main: colours.errorRed,
            contrast: colours.white,
        },
        warning: {
            main: colours.errorRed,
        },
        info: {
            main: colours.lightBlue,
            contrast: colours.white,
        },
        common: {
            background: colours.white,
            accent: colours.lightBlue,
            accentContrast: colours.white,
            border: colours.darkBlue,
            disabled: colours.darkGrey,
        },
        text: {
            main: colours.darkBlue,
            disabled: colours.darkGrey,
            accent: colours.white,
            link: colours.darkBlue,
        },
    },
    focus: {
        color: colours.lightGreen,
    },
    modal: {
        defaultProps: {
            headlineTypographyProps: {
                weight: 700,
                size: styleHelpers.rem(22),
                lineHeight: 1.36,
            },
            closeButtonProps: {
                css: css`
                    border: 2px solid transparent;
                    padding: 0 ${styleHelpers.rem(5)};
                    &:hover,
                    &:active,
                    &:focus {
                        background: none;
                        span {
                            color: ${colours.errorRed};
                        }
                    }
                    width: ${styleHelpers.rem(36)};
                    height: ${styleHelpers.rem(36)};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: transparent;
                `,
            },
            cssOverrides: {
                modalTitle: css`
                    border: none;
                    padding: ${styleHelpers.rem(20)};
                    @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                        padding: ${styleHelpers.rem(20)} ${styleHelpers.rem(30)};
                    }
                `,
                headlineTypography: css`
                    font-family: ${bodyFont};
                `,
                scrim: css`
                    background-color: ${styleHelpers.transparentize(colours.darkBlue, 0.75)};
                `,
                modalBody: css`
                    & {
                        border-radius: ${styleHelpers.rem(10)};
                        height: auto;
                        width: auto;
                        margin: ${styleHelpers.rem(120)} ${styleHelpers.rem(20)};
                        max-width: ${styleHelpers.rem(900)};
                        max-height: calc(100% - 90px);
                        box-shadow: none;
                        @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                            border-radius: ${styleHelpers.rem(15)};
                        }
                    }
                `,
                modalContent: css`
                    padding: 0 ${styleHelpers.rem(20)} ${styleHelpers.rem(20)};
                    @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                        padding: 0 ${styleHelpers.rem(30)} ${styleHelpers.rem(30)};
                    }
                `,
            },
        },
    },
    link: {
        defaultProps: {
            css: css`
                color: ${colours.darkBlue};
                text-decoration: underline;
                font-weight: 600;
                &:hover {
                    text-decoration: none;
                    color: ${colours.green};
                }
                span {
                    color: currentColor !important;
                }
            `,
        },
    },
    button: {
        primary: {
            css: css`
                border: 2px solid ${colours.lightGreen};
                background-color: ${colours.lightGreen};
                color: ${colours.black};
                :hover {
                    background-color: ${colours.white};
                    color: ${colours.black};
                    fill: ${colours.black};
                    border: 2px solid ${colours.lightGreen};
                    svg {
                        color: currentColor;
                    }
                }
            `,
        },
        secondary: {
            css: css`
                border: 2px solid ${colours.darkBlue};
                background-color: ${colours.darkBlue};
                color: ${colours.lightBlue};
                :hover {
                    background-color: ${colours.white};
                    color: ${colours.darkBlue}!important;
                    border: 2px solid ${colours.darkBlue};
                }
            `,
        },
        tertiary: {
            css: css`
                background-color: ${colours.white};
                color: ${colours.darkBlue};
                border: 2px solid ${colours.lightBlue};
                :hover {
                    background-color: ${colours.lightBlue};
                    color: ${colours.darkBlue};
                    border: 2px solid ${colours.darkBlue};
                }
            `,
        },
    },
    breadcrumbs: {
        defaultProps: {
            typographyProps: {
                size: styleHelpers.rem(14),
            },
        },
    },
    typography: {
        fontFamilyImportUrl,
        body: {
            fontFamily: bodyFont,
            weight: 300,
            size: "1rem",
            lineHeight: 1.625,
        },
        p: {
            size: "1rem",
        },
        h1: {
            size: styleHelpers.rem(28),
            lineHeight: 1.25,
            css: css`
                @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                    font-size: ${styleHelpers.rem(48)};
                    line-height: 1.25;
                )}
            `,
        },
        h2: {
            weight: 700,
            size: styleHelpers.rem(28),
            lineHeight: styleHelpers.rem(32),
            css: css`
                @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                    font-size: ${styleHelpers.rem(32)};
                    line-height: 1.25;
                )}
            `,
        },
        h3: {
            weight: 700,
            size: styleHelpers.rem(20),
            lineHeight: styleHelpers.rem(26),
            css: css`
                @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                    font-size: ${styleHelpers.rem(24)};
                    line-height: 1.25;
                )}
            `,
        },
        h4: {
            weight: 700,
            size: styleHelpers.rem(18),
            lineHeight: styleHelpers.rem(24),
            css: css`
                @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                    font-size: ${styleHelpers.rem(20)};
                    line-height: 1.25;
                )}
            `,
        },
        h5: {
            weight: 700,
            size: styleHelpers.rem(18),
            lineHeight: styleHelpers.rem(26),
        },
        h6: {
            weight: 700,
            size: styleHelpers.rem(18),
            lineHeight: styleHelpers.rem(26),
        },
        headerPrimary: {
            fontFamily: headerFont,
            weight: 700,
            size: styleHelpers.rem(36),
            lineHeight: styleHelpers.rem(44),
        },
        headerSecondary: {
            fontFamily: headerFont,
            weight: 700,
            size: styleHelpers.rem(26),
            lineHeight: styleHelpers.rem(34),
        },
        headerTertiary: {
            fontFamily: headerFont,
            weight: 700,
            size: styleHelpers.rem(20),
            lineHeight: styleHelpers.rem(28),
        },
    },
    tag: {
        defaultProps: {
            color: colours.lightBlue,
            iconProps: {
                size: 16,
            },
            typographyProps: {
                weight: 400,
                size: styleHelpers.rem(15),
                lineHeight: styleHelpers.rem(20),
                color: colours.darkBlue,
            },
            css: css`
                padding: 12px 10px 12px 10px;
                border-radius: 0;
                align-items: center;
                button {
                    min-height: 20px;
                    border-radius: 0;
                    padding: 0;
                    margin: 0;
                }
            `,
        },
    },
    pagination: {
        defaultProps: {
            buttonProps: {
                activeMode: "none",
                hoverMode: "none",
                shape: "rectangle",
                css: css`
                    & {
                        background: none;
                        border: none;
                        width: ${styleHelpers.rem(46)};
                        height: ${styleHelpers.rem(46)};
                    }
                    &:not(:disabled):hover,
                    &:not(:disabled):focus {
                        background-color: ${colours.lightBlue};
                    }
                `,
                typographyProps: {
                    weight: 400,
                    size: styleHelpers.rem(16),
                    lineHeight: 1,
                    color: colours.darkBlue,
                },
            },
            cssOverrides: {
                currentButton: css`
                    & {
                        background: none;
                        font-weight: 700;
                        color: ${colours.darkBlue};
                        border-bottom: ${styleHelpers.rem(2)} solid ${colours.darkBlue};
                    }
                    &&:hover {
                        background: ${colours.lightBlue};
                        color: ${colours.darkBlue};
                        border: ${colours.darkBlue};
                        border-bottom: ${styleHelpers.rem(2)} solid ${colours.darkBlue};
                    }
                `,
                linkList: css`
                    & ul {
                        li:nth-child(1),
                        li:nth-child(2),
                        li:nth-last-child(1),
                        li:nth-last-child(2) {
                            button svg {
                                color: ${colours.darkBlue};
                            }
                            button:not(:disabled):hover,
                            button:not(:disabled):focus {
                                background: none;
                                svg {
                                    color: ${colours.lightBlue};
                                }
                            }
                        }
                    }
                `,
            },
            selectProps: {
                cssOverrides: {
                    inputSelect: css`
                        border: none;
                    `,
                    formField: css`
                        && label {
                            color: ${colours.darkBlue};
                            padding-top: ${styleHelpers.rem(15)};
                        }
                    `,
                    formInputWrapper: css`
                        height: ${styleHelpers.rem(54)};
                        display: flex;
                        align-items: center;
                        color: ${colours.darkBlue};
                        border: solid ${styleHelpers.rem(1)} ${colours.darkBlue};
                        && span {
                            color: ${colours.darkBlue};
                            top: ${styleHelpers.rem(9)};
                        }
                    `,
                },
            },
        },
    },
    toast: {
        defaultProps: {
            cssOverrides: {
                toast: css`
                    box-shadow: none;
                    border: ${styleHelpers.rem(1)} solid ${colours.darkGrey};
                    width: ${styleHelpers.rem(280)};
                    padding: ${styleHelpers.rem(15)} ${styleHelpers.rem(10)};
                    @media (min-width: ${styleHelpers.rem(768)}) {
                        width: ${styleHelpers.rem(452)};
                    }
                    button {
                        background-color: transparent;
                        border: 0;
                        padding: 0;
                        top: ${styleHelpers.rem(12)};
                        justify-content: center;
                        &:hover,
                        &:focus,
                        &:focus-visible {
                            border: 0;
                            background: none;
                            svg {
                                color: ${colours.lightGreen};
                            }
                        }
                    }
                `,
                toastBody: css`
                    & {
                        margin: ${styleHelpers.rem(10)} 0 0;
                        padding: 0 ${styleHelpers.rem(30)} 0 0;
                        align-items: flex-start;
                    }
                    span {
                        font-size: ${styleHelpers.rem(16)};
                        font-weight: 300;
                        &[class*="IconWrapper"] {
                            width: ${styleHelpers.rem(28)};
                            height: ${styleHelpers.rem(28)};
                            margin: 0 ${styleHelpers.rem(15)} 0 0;
                        }
                    }
                `,
            },
        },
    },
    accordion: {
        defaultProps: {
            css: css`
                border: none;
                border-bottom: ${styleHelpers.rem(1)} solid ${colours.darkGrey}; ;
            `,
        },
        sectionDefaultProps: {
            titleTypographyProps: {
                color: colours.darkBlue,
                size: styleHelpers.rem(15),
                lineHeight: 1.333,
                weight: 500,
            },
            panelProps: {
                css: css`
                    border: none;
                    padding: ${styleHelpers.rem(5)} ${styleHelpers.rem(20)} ${styleHelpers.rem(40)}
                        ${styleHelpers.rem(5)};
                    p {
                        font-size: ${styleHelpers.rem(15)};
                        line-height: 1.5625;
                    }
                    & p:last-child {
                        margin: 0;
                    }
                `,
            },
        },
    },
    dataTable: {
        defaultProps: {
            cssOverrides: {
                header: css`
                    background-color: ${colours.darkBlue};
                    color: ${colours.white};
                    padding: ${styleHelpers.rem(24)} ${styleHelpers.rem(12)};
                `,
                head: css`
                    /* outline: solid 2px green; */
                `,
                row: css`
                    /* outline: solid 2px blue; */
                    background-color: ${colours.lightBlue};
                    color: ${colours.darkBlue};
                    border-bottom: ${styleHelpers.rem(3)} solid ${colours.white};
                `,
                evenRow: css`
                    /* outline: solid 2px blue; */
                    background-color: ${colours.lightBlue};
                    color: ${colours.darkBlue};
                    border-bottom: ${styleHelpers.rem(3)} solid ${colours.white};
                `,
                cell: css`
                    padding: ${styleHelpers.rem(14)} ${styleHelpers.rem(12)};
                `,
            },
        },
    },
    loadingSpinner: {
        defaultProps: {
            color: colours.darkBlue,
        },
    },
    overflowMenu: {
        defaultProps: {
            cssOverrides: {
                wrapper: css`
                    button {
                        border: solid 1px ${colours.darkGrey};
                        svg {
                            fill: ${colours.darkBlue};
                            color: ${colours.darkBlue};
                        }
                    }
                    button:hover,
                    button:focus {
                        outline: none;
                        border: solid 1px ${colours.darkGrey};
                        background: ${colours.darkGrey};
                        svg {
                            fill: ${colours.darkBlue};
                            color: ${colours.darkBlue};
                        }
                    }
                    button:active {
                        border: solid 1px ${colours.lightBlue};
                    }
                `,
                menuItem: css`
                    &:hover {
                        background: ${colours.lightBlue};
                    }
                `,
            },
        },
    },
    textArea: {
        defaultProps: {
            mergeCss: true,
            cssOverrides: {
                inputSelect: css`
                    border: ${styleHelpers.rem(2)} solid ${colours.darkBlue};
                    border-radius: 5px !important;
                    padding: ${styleHelpers.rem(10)} ${styleHelpers.rem(15)} !important;
                    min-height: 48px;
                    font-size: ${styleHelpers.rem(16)};
                    &:focus {
                        border: ${styleHelpers.rem(2)} solid ${colours.darkBlue} !important;
                        border-radius: 5px;
                        box-shadow: 0 0 3px 2px ${colours.lightGreen};
                        background-color: ${colours.lightBlue20};
                    }
                    &:disabled {
                        border: ${styleHelpers.rem(2)} solid ${colours.darkBlue};
                        background: ${`${colours.darkBlue}33`};
                    }
                `,
            },
        },
    },
    formField: {
        defaultProps: {
            labelProps: {
                weight: 400,
                size: styleHelpers.rem(16),
                lineHeight: 1.25,
            },
            hintProps: {
                weight: 400,
                size: styleHelpers.rem(16),
                lineHeight: 1.25,
                color: colours.darkGrey,
            },
            errorProps: {
                weight: 400,
                size: styleHelpers.rem(16),
                lineHeight: 1.25,
            },
            cssOverrides: {
                formField: css`
                    &&& {
                        label {
                            font-size: ${styleHelpers.rem(16)};
                            color: ${colours.black};
                            font-weight: 600;
                            @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                                font-size: ${styleHelpers.rem(20)};
                            }
                        }
                        span,
                        button {
                            height: 100%;
                        }
                    }
                `,
                inputSelect: css`
                    border: ${styleHelpers.rem(2)} solid ${colours.darkBlue};
                    border-radius: 5px !important;
                    padding: ${styleHelpers.rem(10)} ${styleHelpers.rem(15)};
                    min-height: 48px;
                    font-size: ${styleHelpers.rem(16)};
                    &:focus {
                        border: ${styleHelpers.rem(2)} solid ${colours.darkBlue} !important;
                        border-radius: 5px;
                        box-shadow: 0 0 3px 2px ${colours.lightGreen};
                        background-color: ${colours.lightBlue20};
                        padding: ${styleHelpers.rem(10)} ${styleHelpers.rem(15)};
                    }
                    &:disabled {
                        border: ${styleHelpers.rem(2)} solid ${colours.darkBlue};
                        background: ${`${colours.darkBlue}33`};
                    }
                `,
            },
        },
    },
    checkbox: {
        defaultProps: {
            css: css`
                && span[role="checkbox"],
                && span[role="checkbox"] svg {
                    border: ${styleHelpers.rem(1)} solid ${colours.darkBlue};
                    border-radius: 0;
                    width: ${styleHelpers.rem(24)};
                    height: ${styleHelpers.rem(24)};
                }
            `,
            typographyProps: {
                fontFamily: bodyFont,
                weight: 400,
                size: styleHelpers.rem(16),
                lineHeight: 1.25,
            },
        },
    },
    radio: {
        defaultProps: {
            css: css`
                & {
                    display: flex;
                }
                & input {
                    width: 7px;
                }
                & input + label::before {
                    width: 28px;
                    height: 28px;
                    flex: 0 0 28px;
                    margin: 0 10px auto 0;
                    border: 1px solid ${colours.darkBlue} !important;
                    background: ${colours.white} !important;
                }
                & input:checked + label::after {
                    width: 16px;
                    height: 16px;
                    flex: 0 0 16px;
                    background: ${colours.darkBlue};
                    left: 6px;
                    top: 6px;
                }
            `,
        },
    },
    tooltip: {
        defaultProps: {
            cssOverrides: {
                tooltipBody: css`
                    background-color: ${colours.white};
                    border: solid ${styleHelpers.rem(2)} ${colours.darkBlue};
                    border-top: solid ${styleHelpers.rem(3)} ${colours.darkBlue};
                    border-radius: 0;
                    text-align: start;
                    box-shadow: 0px 0px ${styleHelpers.rem(10)} rgba(0, 0, 0, 0.15);
                `,
                tooltipContainer: css`
                    margin-left: -${styleHelpers.rem(54)};
                    margin-bottom: ${styleHelpers.rem(13)};
                    margin: 0 0 ${styleHelpers.rem(20)} -${styleHelpers.rem(54)};
                    &::after {
                        width: ${styleHelpers.rem(25)};
                        height: ${styleHelpers.rem(25)};
                        background-color: ${colours.white};
                        top: calc(100% - ${styleHelpers.rem(13)});
                        border-width: 0;
                        border-color: 0;
                        border-bottom: solid ${styleHelpers.rem(2)} ${colours.darkBlue};
                        border-right: solid ${styleHelpers.rem(2)} ${colours.darkBlue};
                        left: 25%;
                        transform: rotate(45deg);
                        margin: 0 0 0 -${styleHelpers.rem(13)};
                    }
                `,
                tooltipClickable: css`
                    &:hover,
                    &:focus {
                        outline: 0;
                        svg {
                            color: ${colours.lightBlue};
                        }
                    }
                `,
            },
            iconProps: {
                color: colours.darkBlue,
                src: Info,
            },
            typographyProps: {
                color: colours.darkBlue,
                size: styleHelpers.rem(14),
                lineHeight: 1.428,
                weight: 500,
            },
        },
    },
});

setPostStyleGuideTheme({
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 320, 768, 1024, 1280], // breakpoints
        maxWidths: [1040 + 120 * 2, 1040 + 120 * 2, 1040 + 120 * 2, 1040 + 120 * 2, 1040 + 120 * 2],
    },
});
